import React, { useEffect, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import "./userDashboard.css";

function UserDashboard() {
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const vehiclesPerPage = 6;
    const navigate = useNavigate();

    const customerName = vehicles[0]?.customerName || "User";

    useEffect(() => {
        const token = localStorage.getItem("userId");

        if (!token) {
            console.error("No token found in localStorage");
            setLoading(false);
            return;
        }

        let email = "";

        try {
            const decoded = jwtDecode(token);
            email = decoded.email;
        } catch (error) {
            console.error("Error decoding token:", error);
            setLoading(false);
            return;
        }

        axios.get(`${process.env.REACT_APP_BASE_API_URL}/getUser/api/monthlyParkingUser/${email}`)
            .then((res) => {
                setVehicles(res.data ? [res.data] : []);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching vehicles:", err);
                setLoading(false);
            });
    }, []);

    // Search filter
    const filteredVehicles = vehicles.filter(vehicle =>
        vehicle.vehicleNumber.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Pagination logic
    const totalPages = Math.ceil(filteredVehicles.length / vehiclesPerPage);
    const indexOfLast = currentPage * vehiclesPerPage;
    const indexOfFirst = indexOfLast - vehiclesPerPage;
    const currentVehicles = filteredVehicles.slice(indexOfFirst, indexOfLast);

    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    // ✅ Navigate to details
    const goToUserDetails = (id) => {
        navigate(`/userDashboard/vehicleDetail/${id}`);
    };

    const handleLogout = () => {
        localStorage.removeItem("userId"); // remove token
        navigate("/userLogin");
    };

    return (
        <div className="container mt-5 user-dashboard">
            {/* 🔒 Logout button */}
            <div className="d-flex justify-content-between align-items-center p-3">
                {/* Back Button on Left */}
                <button className="btn btn-secondary" onClick={() => navigate(-1)}>
                    ⬅ Back
                </button>

                {/* Logout Button on Right */}
                <button className="btn btn-danger" onClick={handleLogout}>
                    <i className="bi bi-box-arrow-right me-2"></i>Logout
                </button>
            </div>

            {/* Search */}
            <div className="d-flex flex-column align-items-center">
                <label className="mb-2 fw-bold">Search Vehicle</label>
                <div className="input-group mb-3" style={{ width: "400px" }}>
                    <input
                        className="form-control"
                        type="search"
                        placeholder="Search by Vehicle Number"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button className="btn btn-primary" type="button">
                        <i className="bi bi-search"></i>
                    </button>
                </div>
            </div>

            {/* Vehicles Grid */}
            {loading ? (
                <p className="text-center">Loading your vehicle data...</p>
            ) : (
                <>
                    <h4 className="text-center mb-4">
                        You have currently parked {vehicles.length} vehicle{vehicles.length !== 1 ? "s" : ""}
                    </h4>

                    <div className="row">
                        {currentVehicles.length > 0 ? (
                            currentVehicles.map((vehicle, index) => (
                                <div key={index} className="col-lg-4 col-md-6 col-sm-12 mb-4">
                                    <div className="card p-3 shadow-sm text-center">
                                        <div className="image mb-3">
                                            <img
                                                src={`${process.env.REACT_APP_BASE_API_URL}/monthlyUploads/${vehicle?.vehicleImage?.split("\\").pop()}`}
                                                alt="Vehicle"
                                                className="rounded-circle img-fluid"
                                                style={{ width: "150px", height: "150px", objectFit: "cover" }}
                                            />
                                        </div>
                                        <h5 className="card-title">{vehicle.customerName}</h5>
                                        <p><strong>Vehicle No:</strong> {vehicle.vehicleNumber}</p>
                                        <p><strong>Slot:</strong> {vehicle.parkingSlot}</p>
                                        <p><strong>Rental:</strong> ₹{vehicle.rentalAmount}</p>
                                        <p><strong>Booked Date:</strong> {vehicle.bookedDate || vehicle.createdAt}</p>
                                        <button
                                            className="btn btn-outline-primary mt-2"
                                            onClick={() => goToUserDetails(vehicle._id)}
                                        >
                                            View More
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center mt-4">
                                <h5>No vehicles found</h5>
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    {filteredVehicles.length > vehiclesPerPage && (
                        <nav>
                            <ul className="pagination justify-content-center">
                                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                    <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                                        Previous
                                    </button>
                                </li>

                                {Array.from({ length: totalPages }, (_, index) => (
                                    <li
                                        key={index}
                                        className={`page-item ${currentPage === index + 1 ? "active" : ""}`}
                                    >
                                        <button className="page-link" onClick={() => handlePageChange(index + 1)}>
                                            {index + 1}
                                        </button>
                                    </li>
                                ))}

                                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                    <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                                        Next
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    )}
                </>
            )}
        </div>
    );
}

export default UserDashboard;
